import { Capacitor } from '@capacitor/core';
import { environment } from '../environments/environment';

/**
 * Well-known School Connect web hosts across environments. We primarily match the
 * running build's own baseUrl host, but a post authored in one environment may be
 * viewed in another's app build, so we also recognise the standard app hostnames.
 */
const KNOWN_APP_HOSTS = /^(app|app-qa|app-dev|app-stage|theapppilot)\.nhaschools\.com$/i;

/**
 * Paths that must NOT be hijacked into the SPA router — they are handled by the
 * server (`/api`, `/s/{code}` short links) or the auth flow (`/callback`, `/logout`).
 */
const NON_ROUTABLE = /^\/(api|s\/[A-Za-z0-9]{6}|callback|logout)(\/|$|\?|#)/i;

/** True only when running inside the Capacitor native app (iOS/Android). */
export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

function ownAppHost(): string | null {
  try {
    return new URL(environment.baseUrl).host.toLowerCase();
  } catch {
    return null;
  }
}

/**
 * If `rawHref` is an absolute link back into the School Connect app (our own
 * domain), return the in-app route to hand to the Angular Router
 * (pathname + search + hash). Returns null for external links, non-http(s)
 * schemes, server/auth paths, or anything unparseable.
 */
export function getInternalAppRoute(rawHref: string | null | undefined): string | null {
  if (!rawHref || !/^https?:\/\//i.test(rawHref)) return null;

  let url: URL;
  try {
    url = new URL(rawHref);
  } catch {
    return null;
  }

  const host = url.host.toLowerCase();
  if (host !== ownAppHost() && !KNOWN_APP_HOSTS.test(host)) return null;

  const path = (url.pathname || '/') + url.search + url.hash;
  if (NON_ROUTABLE.test(path)) return null;

  return path;
}
